import React from "react";
import { ASSETS } from "../lib/assets";
import { CTA, Eyebrow } from "../components/CTA";
import { FileText, ShieldCheck, HardHat } from "lucide-react";
import { useReveal, useCountUp } from "../lib/hooks";
import AlliesSection from "../components/AlliesSection";

export default function Home() {
  return (
    <main data-testid="home-page" className="bg-[var(--pc-bg)]">
      <HeroSection />
      <BaruSection />
      <PhilosophySection />
      <ExperienceSection />
      <AssuranceSection />
      <AlliesSection />
      <ClosingSection />
    </main>
  );
}

/* ============================== HERO ============================== */
function HeroSection() {
  return (
    <section
      data-testid="hero-section"
      className="relative w-full overflow-hidden"
      style={{ minHeight: "100vh" }}
    >
      <div className="absolute inset-0">
        <img
          src={ASSETS.heroVilla}
          alt="Residencia en Barú"
          className="w-full h-full object-cover"
          draggable="false"
        />
        <div className="absolute inset-0 hero-shade" />
      </div>

      <div
        className="relative z-10 pc-container min-h-screen flex flex-col justify-center"
        style={{ paddingTop: "120px", paddingBottom: "80px" }}
      >
        <div style={{ maxWidth: "620px" }}>
          <h1
            data-testid="hero-headline"
            className="headline-xl fade-up"
            style={{
              fontSize: "clamp(31px, 3.7vw, 55px)",
              lineHeight: 1.14,
              letterSpacing: "0.18em",
            }}
          >
            Diseñar<br />
            es elegir<br />
            qué permanece.
          </h1>
          <p
            data-testid="hero-subtitle"
            className="body-soft mt-10 max-w-[480px] fade-up d1"
            style={{ fontSize: "15.5px" }}
          >
            Residencias y enclaves de autor<br />en Barú, en el Caribe colombiano.
          </p>
          <div className="mt-12 fade-up d2">
            <CTA testId="hero-cta" to="/contacto">
              Inicia una conversación privada
            </CTA>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================== BARÚ (mapa full-bleed) ============================== */
function BaruSection() {
  const head = useReveal();
  const body = useReveal();
  const stats = useReveal({ threshold: 0.25 });
  const cta = useReveal();
  const map = useReveal({ threshold: 0.08 });

  return (
    <section
      data-testid="baru-section"
      className="relative bg-[var(--pc-bg)] border-t border-[var(--pc-line)] overflow-hidden"
    >
      {/* Full-bleed map background — designed to occupy the entire section */}
      <div
        ref={map.ref}
        className={`absolute inset-0 reveal-img ${map.revealed ? "is-in" : ""}`}
        aria-hidden="true"
      >
        <img
          src={ASSETS.baruMap}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          draggable="false"
        />
        {/* Left-side dark scrim so text reads cleanly on top of the map */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(7,7,7,0.96) 0%, rgba(7,7,7,0.85) 22%, rgba(7,7,7,0.45) 42%, rgba(7,7,7,0.10) 62%, rgba(7,7,7,0) 80%)",
          }}
        />
        {/* Subtle top/bottom vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(7,7,7,0.35) 0%, transparent 14%, transparent 86%, rgba(7,7,7,0.6) 100%)",
          }}
        />
      </div>

      <div className="relative z-10 pc-container section-pad-y grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center min-h-[640px]">
        <div className="lg:col-span-5">
          <div ref={head.ref} className={`reveal ${head.revealed ? "is-in" : ""}`}>
            <Eyebrow testId="baru-eyebrow">Barú</Eyebrow>
            <h2
              className="headline-lg mt-8"
              style={{ fontSize: "clamp(24px, 2.4vw, 35px)", maxWidth: 500 }}
            >
              La tierra frente al Caribe no crece.
            </h2>
          </div>

          <p
            ref={body.ref}
            className={`body-soft mt-9 max-w-[470px] reveal delay-1 ${body.revealed ? "is-in" : ""}`}
          >
            Barú no es solo paisaje. Es suelo limitado, a minutos de Cartagena,
            y una de las decisiones patrimoniales más escasas del Caribe colombiano.
            La mitad de la isla es patrimonio intocable; lo que queda es poco,
            y lo poco perdura.
          </p>

          <div
            ref={stats.ref}
            className="mt-14 grid grid-cols-3 gap-5 max-w-[480px]"
          >
            <Stat value="50%" label="Suelo protegido" show={stats.revealed} delayCls="delay-1" />
            <Stat value="Limitada" label="Tierra edificable" show={stats.revealed} delayCls="delay-2" />
            <Stat value="35 min" label="Desde Cartagena" show={stats.revealed} delayCls="delay-3" />
          </div>

          <div
            ref={cta.ref}
            className={`mt-14 reveal delay-2 ${cta.revealed ? "is-in" : ""}`}
          >
            <CTA testId="baru-cta" to="/baru">Conversemos sobre Barú</CTA>
          </div>
        </div>

        {/* Empty right column — the map occupies the section background */}
        <div className="lg:col-span-7 hidden lg:block" aria-hidden="true" />
      </div>
    </section>
  );
}

/* ============================== PHILOSOPHY ============================== */
function PhilosophySection() {
  const eyebrow = useReveal();
  return (
    <section data-testid="vive-section" className="border-t border-[var(--pc-line)]">
      <div className="pc-container pt-24 md:pt-20 pb-2">
        <div ref={eyebrow.ref} className={`reveal ${eyebrow.revealed ? "is-in" : ""}`}>
          <Eyebrow>Vive sin ruido.</Eyebrow>
        </div>
      </div>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3">
        <PhilosophyPanel
          testId="pillar-1"
          image={ASSETS.pillarBrisa}
          title="Deja entrar la brisa."
          body="La casa respira contigo."
          delay={0}
        />
        <PhilosophyPanel
          testId="pillar-2"
          image={ASSETS.pillarMateria}
          title="Elige materia real."
          body="Piedra, madera, acero. Sin disfraces."
          delay={1}
        />
        <PhilosophyPanel
          testId="pillar-3"
          image={ASSETS.pillarCalma}
          title="Habita la calma."
          body="El lujo no se muestra. Se siente."
          delay={2}
        />
      </div>
    </section>
  );
}

/* ============================== EXPERIENCE — dramatic 700.000 m² ============================== */
function ExperienceSection() {
  const head = useReveal();
  const body = useReveal();
  const cta = useReveal();
  const stat = useReveal({ threshold: 0.45 });
  const cats = useReveal({ threshold: 0.3 });
  const count = useCountUp(700000, { duration: 2400 });

  // Bind stat reveal AND count-up to the same node
  const setStatNode = (el) => {
    stat.ref.current = el;
    count.ref.current = el;
  };

  return (
    <section
      data-testid="paco-section"
      className="border-t border-[var(--pc-line)] bg-[var(--pc-bg)] relative overflow-hidden"
    >
      <div className="pc-container section-pad-y grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Left col — title + body + CTA */}
        <div className="lg:col-span-5">
          <div ref={head.ref} className={`reveal ${head.revealed ? "is-in" : ""}`}>
            <h2
              className="headline-lg"
              style={{ fontSize: "clamp(24px, 2.4vw, 33px)", maxWidth: 460 }}
            >
              Construye con quien sabe resolver.
            </h2>
          </div>
          <p
            ref={body.ref}
            className={`body-soft mt-9 max-w-[440px] reveal delay-1 ${body.revealed ? "is-in" : ""}`}
          >
            Elegir a Paco Cabrera es elegir diseño, criterio técnico y el
            oficio para llevar una idea compleja hasta la obra
            <span className="text-[var(--pc-text)]"> —con calma y precisión—</span>.
          </p>
          <div
            ref={cta.ref}
            className={`mt-14 reveal delay-2 ${cta.revealed ? "is-in" : ""}`}
          >
            <CTA testId="paco-cta" to="/contacto">
              Conversemos sobre tu proyecto
            </CTA>
          </div>
        </div>

        {/* Right col — DRAMATIC 700.000 m² stat */}
        <div className="lg:col-span-7">
          <div
            ref={setStatNode}
            className={`stat-hero ${stat.revealed ? "is-in" : ""}`}
          >
            <div className="stat-hero-wrap">
              <span
                data-testid="stat-counter"
                className="stat-hero-num tabular"
              >
                {count.display}
              </span>
              <span className="stat-hero-unit">m²</span>
            </div>
            {/* Hairline that draws in on reveal */}
            <div className="stat-hero-line" />
            <p
              className="label-small max-w-md leading-[1.7] mt-7"
              style={{ fontSize: 12.5 }}
            >
              Proyectados y construidos
              <br />en siete años.
            </p>
          </div>

          {/* Categories — re-balanced 3-column grid with hairline separators */}
          <div
            ref={cats.ref}
            className="mt-14 grid grid-cols-3 gap-0 border-t border-[var(--pc-line)] pt-9"
          >
            <CategoryItem
              num="01"
              label={["Residencias", "privadas"]}
              show={cats.revealed}
              delayCls="delay-1"
            />
            <CategoryItem
              num="02"
              label={["Enclaves", "de lujo"]}
              show={cats.revealed}
              delayCls="delay-2"
              divider
            />
            <CategoryItem
              num="03"
              label={["Proyectos de alto", "impacto en Barú"]}
              show={cats.revealed}
              delayCls="delay-3"
              divider
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================== ASSURANCE (with relieve background) ============================== */
function AssuranceSection() {
  const head = useReveal();
  const body = useReveal();
  const items = useReveal({ threshold: 0.25 });

  return (
    <section
      data-testid="respaldo-section"
      className="relative border-t border-[var(--pc-line)] overflow-hidden"
    >
      {/* Relieve image as full-bleed background */}
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src={ASSETS.relieveBaru}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          draggable="false"
        />
        {/* Strong dark scrim — keeps content legible while letting the relief breathe on the right */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(7,7,7,0.95) 0%, rgba(7,7,7,0.78) 28%, rgba(7,7,7,0.45) 50%, rgba(7,7,7,0.35) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(7,7,7,0.4) 0%, transparent 14%, transparent 84%, rgba(7,7,7,0.65) 100%)",
          }}
        />
      </div>

      <div className="relative z-10 pc-container section-pad-y grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
        <div className="lg:col-span-4">
          <div ref={head.ref} className={`reveal ${head.revealed ? "is-in" : ""}`}>
            <h2
              className="headline-lg"
              style={{ fontSize: "clamp(22px, 2.2vw, 31px)", maxWidth: 380 }}
            >
              Una inversión blindada.
            </h2>
          </div>
          <p
            ref={body.ref}
            className={`body-soft mt-8 max-w-[420px] reveal delay-1 ${body.revealed ? "is-in" : ""}`}
          >
            Cada proyecto avanza con licencia aprobada, escrituración completa
            e interventoría. Lo que se promete, se cumple.
          </p>
        </div>
        <div ref={items.ref} className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-10">
          <Pledge icon={<FileText size={30} strokeWidth={0.9} />} label="Licencia de curaduría" show={items.revealed} delayCls="delay-1" />
          <Pledge icon={<ShieldCheck size={30} strokeWidth={0.9} />} label="Escrituración y tradición" show={items.revealed} delayCls="delay-2" />
          <Pledge icon={<HardHat size={30} strokeWidth={0.9} />} label="Interventoría de obra" show={items.revealed} delayCls="delay-3" />
        </div>
      </div>
    </section>
  );
}

/* ============================== CLOSING (VA apts 40 interior) ============================== */
function ClosingSection() {
  const head = useReveal();
  const body = useReveal();
  const cta = useReveal();

  return (
    <section
      data-testid="cierre-section"
      className="relative overflow-hidden border-t border-[var(--pc-line)]"
    >
      <div className="relative w-full" style={{ minHeight: "680px" }}>
        <img
          src={ASSETS.cierreInterior}
          alt="Interior del apartamento al atardecer"
          className="absolute inset-0 w-full h-full object-cover"
          draggable="false"
        />
        {/* Black-to-transparent fade on the left where the text lives */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(7,7,7,0.98) 0%, rgba(7,7,7,0.92) 26%, rgba(7,7,7,0.55) 44%, rgba(7,7,7,0.15) 62%, rgba(7,7,7,0) 80%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(7,7,7,0.25) 0%, transparent 14%, transparent 80%, rgba(7,7,7,0.95) 100%)",
          }}
        />

        <div className="relative z-10 pc-container py-24 md:py-32 lg:py-40">
          <div className="max-w-[520px]">
            <div ref={head.ref} className={`reveal ${head.revealed ? "is-in" : ""}`}>
              <h2
                className="headline-lg"
                style={{ fontSize: "clamp(24px, 2.4vw, 35px)" }}
              >
                Iniciemos una conversación privada.
              </h2>
            </div>
            <p
              ref={body.ref}
              className={`body-soft mt-9 max-w-[470px] reveal delay-1 ${body.revealed ? "is-in" : ""}`}
            >
              Trabajamos con un grupo reducido de clientes que entienden el
              valor de diseñar con calma, rigor y permanencia.
            </p>
            <div
              ref={cta.ref}
              className={`mt-12 reveal delay-2 ${cta.revealed ? "is-in" : ""}`}
            >
              <CTA testId="cierre-cta" to="/contacto">
                Inicia una conversación privada
              </CTA>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Sub-components -------------------- */

function Stat({ value, label, show, delayCls = "" }) {
  return (
    <div className={`stat-card reveal ${delayCls} ${show ? "is-in" : ""}`}>
      <div
        className="text-[var(--pc-text)]"
        style={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 300,
          fontSize: 20,
          letterSpacing: "0.06em",
        }}
      >
        {value}
      </div>
      <div className="label-small mt-3" style={{ fontSize: 10.5 }}>
        {label}
      </div>
    </div>
  );
}

function PhilosophyPanel({ image, title, body, testId, delay = 0 }) {
  const { ref, revealed } = useReveal({ threshold: 0.2 });
  return (
    <article
      data-testid={testId}
      ref={ref}
      className={`relative img-wrap min-h-[440px] md:min-h-[520px] lg:min-h-[580px] group reveal ${
        revealed ? "is-in" : ""
      } ${delay === 1 ? "delay-1" : ""} ${delay === 2 ? "delay-2" : ""}`}
    >
      <img
        src={image}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        draggable="false"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(7,7,7,.92)] via-[rgba(7,7,7,.45)] to-[rgba(7,7,7,.15)]" />
      <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-10 lg:p-12">
        <h3
          className="headline-md max-w-[300px]"
          style={{
            fontSize: "clamp(16px, 1.4vw, 21px)",
            letterSpacing: "0.16em",
            lineHeight: 1.35,
          }}
        >
          {title}
        </h3>
        <p className="body-soft mt-5 max-w-[280px]" style={{ fontSize: 14.5 }}>
          {body}
        </p>
      </div>
    </article>
  );
}

function CategoryItem({ num, label, show, delayCls = "", divider = false }) {
  return (
    <div
      className={`relative px-2 sm:px-4 lg:px-8 ${divider ? "border-l border-[var(--pc-line)]" : ""}`}
    >
      <div className={`reveal ${delayCls} ${show ? "is-in" : ""}`}>
        <div
          className="text-[var(--pc-bronze)] mb-3"
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 300,
            fontSize: 11,
            letterSpacing: "0.22em",
          }}
        >
          — {num}
        </div>
        <div
          className="text-[var(--pc-text)] leading-[1.55] category-item-label"
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 300,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            wordBreak: "keep-all",
            hyphens: "none",
          }}
        >
          {label.map((l, i) => (
            <React.Fragment key={i}>
              {l}
              {i < label.length - 1 ? <br /> : null}
              {i < label.length - 1 ? " " : null}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function Pledge({ icon, label, show, delayCls = "" }) {
  return (
    <div className={`flex flex-col items-start reveal ${delayCls} ${show ? "is-in" : ""}`}>
      <div className="text-[var(--pc-text)] mb-6 opacity-85">{icon}</div>
      <div className="label-small leading-[1.7]" style={{ fontSize: 11.5 }}>
        {label}
      </div>
    </div>
  );
}
